import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { Flex, Spin } from 'antd';
import { SelectComponent } from 'baseComponents/SelectComponent';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { TextInputComponent } from 'baseComponents/TextInputComponent';
import { useGetFranchiseOwners } from 'hooks/useGetFranchiseOwners';
import { phoneRegex } from 'components/AdminUserForm';

export type TestUserFormValues = {
  phone: string;
  nickname: string;
  email?: string;
  franchiseOwner: number;
};

type AdminTestUserFormProps = {
  onSubmit: (data: TestUserFormValues) => void;
  onCancel?: () => void;
  defaultValues?: Partial<TestUserFormValues>;
  submitButtonLabel?: string;
};

export const userFormSchema = Yup.object().shape({
  phone: Yup.string()
    .required('Phone is required.')
    .matches(
      phoneRegex,
      'Please enter a valid UK phone number (e.g. +447123456789)'
    ),
  nickname: Yup.string().required('Nickname is required.'),
  email: Yup.string(),
  franchiseOwner: Yup.number().required('Franchise owner is required.'),
});

const initialState = {
  phone: '',
  nickname: '',
  email: '',
  franchiseOwner: undefined,
};

export const AdminTestUserForm = ({
  onCancel,
  onSubmit,
  defaultValues,
  submitButtonLabel = 'Submit',
}: AdminTestUserFormProps) => {
  const franchiseOwners = useGetFranchiseOwners();

  const { handleSubmit, control } = useForm<TestUserFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      ...initialState,
      ...defaultValues,
    },
    resolver: yupResolver(userFormSchema),
  });

  const handleSubmitCallback = async (data: TestUserFormValues) => {
    onSubmit(data);
  };

  // TODO: remove, only for dev purposes
  const isStaging =
    process.env.REACT_APP_API_URL === 'https://pjstaging.consultmyapp.com/api';
  // console.log('isStaging', isStaging);

  return (
    <form
      style={{ flex: 1, flexBasis: '100%' }}
      onSubmit={handleSubmit(handleSubmitCallback)}
    >
      <Flex vertical gap={16} style={{ padding: 2 }}>
        <Controller
          name='phone'
          control={control}
          render={({ field, fieldState }) => (
            <TextInputComponent
              label='Phone'
              error={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Controller
          name='nickname'
          control={control}
          render={({ field, fieldState }) => (
            <TextInputComponent
              label='Nickname'
              error={fieldState.error?.message}
              {...field}
            />
          )}
        />

        {isStaging && (
          <Controller
            name='email'
            control={control}
            render={({ field }) => (
              <TextInputComponent
                label='email (only for dev purposes, visible only on staging)'
                {...field}
              />
            )}
          />
        )}

        <Controller
          name='franchiseOwner'
          control={control}
          render={({ field, fieldState }) => (
            <Spin spinning={franchiseOwners.isLoading}>
              <SelectComponent
                placeholder='Franchise owner'
                options={
                  franchiseOwners.data?.map(({ email, id }) => ({
                    label: email,
                    value: id,
                  })) ?? []
                }
                error={
                  fieldState.error?.message ||
                  franchiseOwners.error?.response?.data?.message
                }
                {...field}
              />
            </Spin>
          )}
        />
      </Flex>

      <Flex gap={8} style={{ maxWidth: 648, marginTop: 64 }}>
        <ButtonComponent
          label={submitButtonLabel}
          appereance='primary'
          size='middle'
          fullWidth
          htmlType='submit'
        />
        <ButtonComponent
          label='Cancel'
          onClick={onCancel}
          appereance='secondary'
          size='middle'
          fullWidth
        />
      </Flex>
    </form>
  );
};
